import { Mail, MessageSquare, Megaphone, Layers, Target, TrendingUp, Users } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { fmtEuro, cn } from '@/lib/utils';
import type { Campaign, CampaignChannel, CampaignStatus } from '@/lib/types';

const channelConfig: Record<CampaignChannel, { icon: React.ComponentType<{ className?: string }>; label: string; cls: string }> = {
  email: { icon: Mail, label: 'Email', cls: 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30' },
  sms: { icon: MessageSquare, label: 'SMS', cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30' },
  ads: { icon: Megaphone, label: 'Pub', cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30' },
  mixed: { icon: Layers, label: 'Multi', cls: 'bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30' },
};

const statusVariant: Record<CampaignStatus, BadgeProps['variant']> = {
  draft: 'secondary',
  scheduled: 'info',
  active: 'success',
  completed: 'outline',
  paused: 'warning',
};
const statusLabel: Record<CampaignStatus, string> = {
  draft: 'Brouillon',
  scheduled: 'Programmée',
  active: 'En cours',
  completed: 'Terminée',
  paused: 'En pause',
};

export function CampaignCard({ campaign, onClick, isActive }: { campaign: Campaign; onClick?: () => void; isActive?: boolean }) {
  const c = campaign;
  const ch = channelConfig[c.channel];
  const Icon = ch.icon;

  const revenueTarget = c.kpi_targets.revenue_eur ?? 0;
  const revenueActual = c.kpi_actuals.revenue_eur ?? 0;
  const revenuePct = revenueTarget > 0 ? Math.min(100, (revenueActual / revenueTarget) * 100) : 0;

  const conversionsTarget = c.kpi_targets.conversions ?? 0;
  const conversionsActual = c.kpi_actuals.conversions ?? 0;

  const sent = c.kpi_actuals.sent ?? 0;
  const opened = c.kpi_actuals.opened ?? 0;
  const openRate = sent > 0 ? opened / sent : 0;

  return (
    <Card
      onClick={onClick}
      className={cn('transition-all cursor-pointer hover:shadow-md', isActive && 'ring-2 ring-primary')}
    >
      <CardContent className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className={cn('inline-flex h-7 w-7 items-center justify-center rounded-md border', ch.cls)}>
              <Icon className="h-3.5 w-3.5" />
            </span>
            <div className="font-semibold truncate">{c.name}</div>
          </div>
          <Badge variant={statusVariant[c.status]}>{statusLabel[c.status]}</Badge>
        </div>

        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> {c.audience.size}</span>
          {c.promo_code && <span className="inline-flex items-center gap-1"><Target className="h-3 w-3" /> {c.promo_code} ({c.promo_value_pct ?? 0}%)</span>}
          <span className="ml-auto">{c.start_date.slice(0,10)} → {c.end_date.slice(0,10)}</span>
        </div>

        {revenueTarget > 0 && (
          <div>
            <div className="mb-1 flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Revenue</span>
              <span className="num font-semibold">
                {fmtEuro(revenueActual)} <span className="text-muted-foreground font-normal">/ {fmtEuro(revenueTarget)}</span>
              </span>
            </div>
            <Progress value={revenuePct} indicatorClassName={revenuePct >= 100 ? 'bg-success' : 'bg-info'} />
          </div>
        )}

        <div className="grid grid-cols-3 gap-2 text-center text-xs">
          <Stat label="Conv." value={`${conversionsActual} / ${conversionsTarget || '—'}`} />
          <Stat label="Open rate" value={sent > 0 ? `${Math.round(openRate * 100)}%` : '—'} />
          <Stat label={c.channel === 'ads' ? 'CPA' : 'Bookings'} value={String(c.kpi_actuals.bookings_calcom ?? '—')} icon={<TrendingUp className="h-3 w-3" />} />
        </div>
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, icon }: { label: string; value: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <div className="rounded-md bg-muted/40 p-1.5">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground inline-flex items-center gap-1 justify-center">
        {icon}
        {label}
      </div>
      <div className="num font-semibold">{value}</div>
    </div>
  );
}
